import * as React from 'react';
import { useMemo } from 'react';
import { isBefore, isSameDay, parse, parseISO, subDays, toDate } from 'date-fns';

import { Price, WeekCalendarProps } from '../types';
import { Money } from '../../Money/types';
import { createDatesArray, isDateInRange } from '../utils';
import Day from './Day/Day';
import Slider from './Slider/Slider';
import { Currency } from '../../enums';
import { useTheme } from '../../theme';
import { getDateWithoutTimezone } from '../../Engine/components/FastSearch/utils';
import { useConfig } from '../../context';
import { API_DATE_FORMAT, useIsMobile, useIsTablet } from '../../utils';
import { useRenderers } from '../../renderProps';

const WeekCalendar: React.FC<WeekCalendarProps> = ({
	prices,
	centralDate,
	onRangeChange,
	onSelect,
	currentMinPriceSelectedDate,
	isMilesSearch,
	allowSelectWithoutPrice,
	avaliableDateRange,
	hidePrice,
	minDate: minDateProp,
	loading
}) => {
	const { WeekCalendar: weekCalendarStyles } = useTheme('WeekCalendar');

	const calendarDaysCount = useConfig()?.Engine.calendarDaysCount;
	const OverrideDay = useRenderers().CalendarDay ?? Day;

	const isMobile = useIsMobile();
	const isTablet = useIsTablet();

	const pricesData = useMemo(() => {
		const pricesMap = new Map<number, Price>();

		let minDate: number | null = null;
		let maxDate: number | null = null;
		let minPrice: Money = {
			amount: prices[0]?.price?.amount ?? 0,
			currency: prices[0]?.price?.currency ?? Currency.USD
		};

		prices.forEach(price => {
			const dateTimestamp = new Date(price.date).getTime();

			if (minDate === null || dateTimestamp < minDate) {
				minDate = dateTimestamp;
			}

			if (maxDate === null || dateTimestamp > maxDate) {
				maxDate = dateTimestamp;
			}

			if (price.price && price.price.amount < minPrice.amount) {
				minPrice = { ...price.price };
			}

			pricesMap.set(dateTimestamp, {
				date: price.date instanceof Date ? toDate(price.date) : parseISO(price.date),
				price: price.price
			});
		});

		return { pricesMap, minDate, maxDate, minPrice };
	}, [prices]);

	const { pricesMap, minDate, maxDate, minPrice } = pricesData;

	const dates = useMemo(() => {
		if (minDate === null || maxDate === null) {
			return [];
		}

		return createDatesArray(toDate(minDate), toDate(maxDate));
	}, [minDate, maxDate]);

	const parsedPricesForArrows = useMemo(() => {
		return prices.filter(item => item?.date).map(item => parse(item.date as string, API_DATE_FORMAT, new Date()));
	}, [prices]);

	const { today, yesterday } = useMemo(() => {
		const todayValue = new Date();

		return {
			today: todayValue,
			yesterday: subDays(todayValue, 1)
		};
	}, []);

	const hideDisabledLeftArrow = useMemo(() => {
		const minAvailableDate = avaliableDateRange?.[0];

		return parsedPricesForArrows.some(date => {
			if (isBefore(date, today) && !isSameDay(date, today)) {
				return true;
			}

			return minAvailableDate && (isBefore(date, minAvailableDate) || isSameDay(date, minAvailableDate));
		});
	}, [today, parsedPricesForArrows, avaliableDateRange]);

	const hideDisabledRightArrow = useMemo(() => {
		const maxAvailableDate = avaliableDateRange?.[1];
		if (!maxAvailableDate) {
			return false;
		}

		return parsedPricesForArrows.some(
			date => isBefore(maxAvailableDate, date) || isSameDay(date, maxAvailableDate)
		);
	}, [parsedPricesForArrows, avaliableDateRange]);

	const samePriceAllWeek = useMemo(() => {
		const uniquePrices = new Set(
			[...pricesMap.values()]
				.filter(dateWithPrice => dateWithPrice?.price?.amount)
				.map(dateWithPrice => dateWithPrice.price.amount)
		);

		return uniquePrices.size === 1;
	}, [pricesMap]);

	const selectedDateData = useMemo(() => {
		const centralDateTimestamp = centralDate.getTime();
		const selectedPrice = pricesMap.get(centralDateTimestamp);
		const selectedDatePriceIsMin = selectedPrice?.price?.amount === minPrice.amount;

		return { centralDateTimestamp, selectedDatePriceIsMin };
	}, [centralDate, pricesMap, minPrice.amount]);

	const compareSelectedDates = (date: Date, centralDate?: Date): boolean => {
		if (!centralDate) {
			return false;
		}

		const normalizedDate = getDateWithoutTimezone(date);

		return (
			centralDate.getFullYear() === normalizedDate.getFullYear() &&
			centralDate.getDate() === normalizedDate.getDate() &&
			centralDate.getMonth() === normalizedDate.getMonth()
		);
	};

	const selectedIndex = useMemo(() => {
		const index = dates.findIndex(date => compareSelectedDates(date, centralDate));

		return index >= 0 ? index : 0;
	}, [dates, centralDate]);

	const calculateDayProps = (date: Date) => {
		const timestamp = date.getTime();
		const price = pricesMap.get(timestamp) ?? null;
		const isSelected = compareSelectedDates(date, centralDate);
		const dateWithoutTimezone = getDateWithoutTimezone(date);

		/**
		 * WEBSKY-7248
		 * Подменяем цену для выбранной даты в календаре
		 * на минимальную цену с выдачи результатов поиска
		 */
		const realPrice = isSelected && currentMinPriceSelectedDate ? currentMinPriceSelectedDate : price?.price;

		const isBestPrice = selectedDateData.selectedDatePriceIsMin
			? isSelected
			: !samePriceAllWeek && realPrice?.amount === minPrice.amount;

		const isBeforeMinDate = minDateProp && isBefore(date, minDateProp);
		const canSelectWithoutPrice = (!isBeforeMinDate && isMilesSearch) || allowSelectWithoutPrice;
		const isAvailable = canSelectWithoutPrice ? !isBefore(date, yesterday) : !!realPrice;

		const isDisabled = !isAvailable || !isDateInRange(date, avaliableDateRange);

		const needHidePrice = hidePrice || isMilesSearch;

		return { dateWithoutTimezone, realPrice, isBestPrice, isSelected, isDisabled, needHidePrice };
	};

	const renderDay = (date: Date) => {
		const {
			dateWithoutTimezone,
			realPrice,
			isBestPrice,
			isSelected,
			isDisabled,
			needHidePrice
		} = calculateDayProps(date);

		return (
			<OverrideDay
				className={weekCalendarStyles.day}
				date={dateWithoutTimezone}
				price={realPrice}
				isBestPrice={isBestPrice}
				hidePrice={needHidePrice}
				disabled={isDisabled}
				selected={isSelected}
				loading={loading}
				onSelect={onSelect}
			/>
		);
	};

	if (!prices.length) {
		return null;
	}

	return (
		<Slider
			mode={isTablet ? 'scroll' : 'static'}
			calendarDaysCount={calendarDaysCount}
			selectedIndex={selectedIndex}
			dates={dates}
			centralDate={centralDate}
			loading={loading}
			hideArrows={isMobile}
			disabledLeftArrow={hideDisabledLeftArrow || loading}
			disabledRightArrow={hideDisabledRightArrow || loading}
			minItemWidth={100}
			onRangeChange={onRangeChange}
			renderDay={renderDay}
		/>
	);
};

export default WeekCalendar;
